import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';
import { OrbisLogo } from '@/components/OrbisLogo';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/forgot-password`, { email });
      toast.success(response.data.message);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ background: 'var(--orbis-bg)' }}>
        <div className="w-full max-w-[400px] text-center">
          <OrbisLogo size={32} variant="full" className="mx-auto mb-8" />

          <div className="mb-6">
            <h2 style={{ fontSize: '24px', fontWeight: 200, letterSpacing: '-0.04em', color: 'var(--orbis-warm)', marginBottom: '8px' }}>
              Check your email
            </h2>
            <p style={{ fontSize: '14px', fontWeight: 300, color: '#5A5855', marginBottom: '24px' }}>
              If an account with that email exists, we've sent you a password reset link.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => setSubmitted(false)}
              className="w-full transition-opacity hover:opacity-80"
              style={{
                height: '48px', borderRadius: '100px', border: 'none',
                background: 'var(--orbis-blue)', color: 'var(--orbis-bg)',
                fontSize: '14px', fontWeight: 400, fontFamily: 'var(--orbis-font)',
                cursor: 'pointer',
              }}
            >
              Send another email
            </button>

            <Link to="/login" className="w-full flex items-center justify-center gap-2 transition-opacity hover:opacity-80"
              style={{
                height: '48px', borderRadius: '100px',
                border: '0.5px solid rgba(255,255,255,0.1)', background: '#161616',
                color: 'var(--orbis-warm)', fontSize: '14px', fontWeight: 400,
                fontFamily: 'var(--orbis-font)', textDecoration: 'none',
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: 'var(--orbis-bg)' }}>
      <div className="w-full max-w-[400px]">
        <OrbisLogo size={32} variant="full" className="mx-auto mb-8" />

        <div className="mb-6">
          <h2 style={{ fontSize: '24px', fontWeight: 200, letterSpacing: '-0.04em', color: 'var(--orbis-warm)', marginBottom: '8px' }}>
            Reset your password
          </h2>
          <p style={{ fontSize: '14px', fontWeight: 300, color: '#5A5855' }}>
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <label style={{ fontSize: '11px', fontWeight: 400, fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#5A5855' }}>
              Email
            </label>
            <div className="flex items-center gap-3 px-4" style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '10px', height: '48px' }}>
              <Mail className="w-4 h-4 flex-shrink-0" style={{ color: '#5A5855' }} />
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none"
                style={{ fontSize: '14px', fontWeight: 300, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center justify-center gap-2 w-full transition-opacity"
            style={{
              height: '48px', borderRadius: '100px', border: 'none',
              background: 'var(--orbis-blue)', color: 'var(--orbis-bg)',
              fontSize: '14px', fontWeight: 400, fontFamily: 'var(--orbis-font)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              marginTop: '4px',
            }}
          >
            {isLoading ? (
              <><OrbisSpinner size={16} color="var(--orbis-bg)" />Sending reset email...</>
            ) : (
              <>Send reset email</>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm hover:underline" style={{ color: 'var(--orbis-warm)' }}>
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}